// First-touch acquisition capture.
//
// Ads point at app.prozpects.com with utm_* params; ad platforms also append
// their own click ids (gclid/fbclid/...), which are what let conversions be
// uploaded back so the platform optimises on athletes who actually pay rather
// than on raw signups.
//
// First touch wins and expires after 90 days — without an expiry a click from
// eight months ago would still take credit for the signup.

export const ATTRIBUTION_KEY = 'pz_attribution_v1';
export const ATTRIBUTION_TTL_DAYS = 90;

const PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'ttclid',
  'msclkid',
];

const MAX_VALUE = 200;

function readRecord() {
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.captured_at) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * A record only counts as "already captured" when it parses and is within
 * the TTL. An expired or unparseable record must be treated as absent by
 * both readers — otherwise a stale record would permanently block capture
 * (captureAttribution's guard) while simultaneously reading back as nothing
 * (getAttribution), silently disabling attribution for that browser forever.
 *
 * The age must be bounded on BOTH sides. A device with a wrong clock can write
 * captured_at years in the future; once the clock is corrected, a
 * future-dated record is "younger than 90 days" forever, so capture returns
 * early on every subsequent visit and every real ad click on that browser is
 * discarded for as long as the fake timestamp stays ahead of now. That is the
 * exact "stale record permanently blocks capture" failure this guard exists to
 * prevent, reached through different arithmetic.
 */
function isValid(record, now) {
  if (!record) return false;
  const capturedAt = new Date(record.captured_at);
  const ageDays = (now.getTime() - capturedAt.getTime()) / 86400000;
  return !Number.isNaN(ageDays) && ageDays >= 0 && ageDays <= ATTRIBUTION_TTL_DAYS;
}

/**
 * Capture attribution from a landing URL. No-op when a valid (unexpired,
 * parseable) record already exists. Returns the stored payload, or null when
 * there was nothing worth storing.
 */
export function captureAttribution(search, referrer, now = new Date()) {
  const existing = readRecord();
  if (isValid(existing, now)) return existing.data;

  const params = new URLSearchParams(search || '');
  const data = {};
  PARAM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) data[key] = String(value).slice(0, MAX_VALUE);
  });

  // An external referrer is the only signal for organic traffic, so it is
  // worth storing even with no utm params present.
  const isExternal = referrer && !referrer.includes(window.location.host);
  if (isExternal) data.referrer = String(referrer).slice(0, MAX_VALUE);

  if (Object.keys(data).length === 0) return null;

  // Origin + pathname ONLY — never the query string. A campaign short link
  // carries its utm params through the redirect onto pages whose own query
  // string holds a secret: /reset-password?utm_source=sms&token=<JWT>, and the
  // same on /verify-email, /confirm-email-change, /guardian-accept and
  // /coach-review-action. utm_source alone is enough to make `data` non-empty,
  // so storing href would put that token in localStorage for 90 days and then
  // into users.acquisition on the next registration. The landing PAGE is the
  // analytics signal; its parameters never were.
  data.landing_url = String(window.location.origin + window.location.pathname).slice(0, MAX_VALUE);

  try {
    window.localStorage.setItem(
      ATTRIBUTION_KEY,
      JSON.stringify({ captured_at: now.toISOString(), data }),
    );
  } catch {
    // Storage disabled or full — attribution is best-effort, never blocking.
  }
  return data;
}

/** Stored attribution, or null when absent or older than the TTL. */
export function getAttribution(now = new Date()) {
  const record = readRecord();
  if (!isValid(record, now)) return null;
  return record.data;
}

/**
 * Drop the stored record. Called after a successful registration so the next
 * signup on the same browser starts from a clean slate.
 *
 * First touch wins and nothing else ever clears the record, so without this a
 * camp walk-in registration kiosk or a shared family iPad hands athlete #1's
 * utm_source to every subsequent registration on that browser for 90 days.
 * This product runs literal walk-in registration stations, so it is not
 * hypothetical.
 */
export function clearAttribution() {
  try {
    window.localStorage.removeItem(ATTRIBUTION_KEY);
  } catch {
    // ignore
  }
}
